import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { buildProductSeo, humanizeSlug } from '../lib/catalogSeo';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableRow } from '../components/ui/table';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../components/ui/breadcrumb';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Shield,
  Zap,
  Clock,
  Users,
  FileText,
  Download,
  Award,
  ExternalLink,
  ArrowLeft,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ConsultantChatGate from '../components/ConsultantChatGate';
import CallbackForm from '../components/CallbackForm';
import RequestPriceForm from '../components/RequestPriceForm';
import TaskCTA from '../components/TaskCTA';
import RequestCalculationForm from '../components/RequestCalculationForm';
import { trackExpertVendorClick } from '../lib/metrikaGoals';
import { productsAPI, cartAPI, vendorsAPI, newsAPI } from '../lib/api';
import {
  savePendingCartItem,
  dispatchCartUpdate,
  getPendingCartItems,
  getGuestSessionId,
} from '../lib/pendingCart';
import { useAuth } from '../contexts/AuthContext';
import { navigateToCart, CART_LOGIN_RETURN } from '../lib/cartNavigation';
import { useToast } from '../hooks/use-toast';
import SEOHead from '../components/SEOHead';
import { DEFAULT_PRODUCT_ICON_PATH, getProductIconSrcForProduct, resolveVendorLogo, isProductIconImageUrl } from '../lib/productIcon';
import { splitProductDescription } from '../lib/productDescription';
import ProductVendorTermsNotice from '../components/ProductVendorTermsNotice';
import ProductNewsSidebar from '../components/ProductNewsSidebar';
import { catalogVendorPath } from '../lib/catalogRoutes';
import { solutionsPathForVendorSlug } from '../lib/vendorRoutes';
import {
  currentCatalogHref,
  getCatalogBack,
  inferCatalogParentHref,
  withCatalogBack,
} from '../lib/catalogNavigation';
import { ensureArray } from '../lib/utils';
import {
  resolveFeaturedNewsFromBlog,
  resolveFeaturedProductNews,
} from '../lib/featuredProductNews';
import {
  formatLicensePrice,
  licenseHasNumericPrice,
  buildPriceRequestCalculatorData,
  getLicenseDeliveryTerms,
  getLicenseNumericPrice,
  getLicenseKey,
  buildLicenseCalculatorData,
  resolveLicenseKeysInCart,
  isLicenseInCart,
} from '../lib/productLicenses';

function normalizeProductMaterials(materials) {
  if (!Array.isArray(materials)) return [];
  return materials
    .map((item) => ({
      name: String(item?.name || '').trim(),
      url: String(item?.url || item?.file_url || '').trim(),
    }))
    .filter((item) => item.name && item.url);
}

const DEFAULT_VENDOR_INFO = {
  materials: [],
  deliveryTerms: [
    { condition: 'Срок поставки', value: '1-3 рабочих дня' },
    { condition: 'Способ поставки', value: 'Облачное подключение' },
    { condition: 'Активация', value: 'Автоматическая' },
    { condition: 'Гарантия', value: '99.9% доступности' },
  ],
  additionalServices: [
    { id: 'integration', name: 'Интеграция с системами', description: 'Подключение систем для обмена данными, например CRM, почта, сайты, файлы, ERP и т.д. От двух недель. С вами будет опытный менеджер и интегратор. Пять простых шагов для успешной интеграции: Аудит текущих процессов -> Техническое задание -> Интеграция -> Тестирование -> Запуск.', price: 145500, isMonthly: false},
    { id: 'start-support', name: 'Поддержка сотрудников ', description: 'Тариф Старт. Базовый минимум. Без выделенного NOC - пишете в привычный канал, подключаемся удалённо. До 10 часов консультаций в месяц. Ответ до 8 рабочих часов.', price: 99000, isMonthly: true },
    { id: 'ml-analytics', name: 'Прозрачность расходов', description: 'Тариф ML-аналитика. Контроль затрат на LLM. Всё из Старт + Ежемесячный отчёт и рекомендации по оптимизации. Ответ до 6 рабочих часов.', price: 279000, isMonthly: true },
  ],
  basePricePerUser: 0,
};

// Компонент для логотипа с обработкой ошибок и защитой от мерцания
const CompanyLogo = ({ company }) => {
  const [imageError, setImageError] = useState(false);
  const lastErrorCheckRef = useRef(0);
  const errorTimeoutRef = useRef(null);

  const handleImageError = () => {
    const now = Date.now();
    const timeSinceLastCheck = now - lastErrorCheckRef.current;

    // Проверяем ошибку только если прошло более 5 секунд с последней проверки
    if (timeSinceLastCheck >= 5000) {
      // Очищаем предыдущий таймер, если он есть
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
      
      lastErrorCheckRef.current = now;
      setImageError(true);
    } else {
      // Если проверка была недавно, отменяем предыдущий таймер и устанавливаем новый
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
      
      const remainingTime = 5000 - timeSinceLastCheck;
      errorTimeoutRef.current = setTimeout(() => {
        setImageError(true);
        lastErrorCheckRef.current = Date.now();
      }, remainingTime);
    }
  };

  useEffect(() => {
    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      className="w-32 h-24 flex items-center justify-center bg-card/50 border border-border/30 rounded-lg p-4 hover:border-accent/50 transition-all duration-300 hover:shadow-md"
      title={company.name}
    >
      {company.logo && !imageError ? (
        <img
          src={company.logo}
          alt={company.name}
          className="max-w-full max-h-full object-contain"
          onError={handleImageError}
        />
      ) : (
        <span className="text-xs font-medium text-muted-foreground text-center leading-tight px-2">
          {company.name}
        </span>
      )}
    </div>
  );
};

const ProductPageNew = () => {
  const { id, vendorSlug, solutionSlug } = useParams();
  const location = useLocation();
  const productId = id || solutionSlug;
  const isLegacyProductPath = location.pathname.startsWith('/product/');
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [product, setProduct] = useState(null);
  /** Вендор из каталога (/api/vendors): логотип и полное HTML-описание из админки */
  const [catalogVendor, setCatalogVendor] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expertContactOpen, setExpertContactOpen] = useState(false);
  const [isCalculationFormOpen, setIsCalculationFormOpen] = useState(false);
  const [selectedServices, setSelectedServices] = useState({
    'tech-support-12': true,
  });
  const [selectedRelatedProducts, setSelectedRelatedProducts] = useState({});
  const [addingToCart, setAddingToCart] = useState(false);
  const [addingLicenseKey, setAddingLicenseKey] = useState(null);
  const [licenseCartState, setLicenseCartState] = useState({
    keys: new Set(),
    legacyProductInCart: false,
  });
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [priceRequestOpen, setPriceRequestOpen] = useState(false);
  const [priceRequestLicenseIndex, setPriceRequestLicenseIndex] = useState(null);
  const [selectedServiceForPopup, setSelectedServiceForPopup] = useState(null);
  const [featuredPublication, setFeaturedPublication] = useState(null);
  const productLicenses = Array.isArray(product?.licenses)
    ? product.licenses
    : Array.isArray(product?.calculator_config?.licenses)
      ? product.calculator_config.licenses
      : [];

  // Функция для парсинга цены из строки "от 4 990 ₽/мес"
  const parsePrice = (priceString) => {
    const match = priceString.match(/[\d\s]+/);
    if (match) {
      return parseInt(match[0].replace(/\s/g, ''), 10);
    }
    return 0;
  };

  // Функция для добавления/удаления сопутствующего продукта в заказ
  const handleAddRelatedProduct = (relatedProduct, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectedRelatedProducts[relatedProduct.id]) {
      // Если продукт уже добавлен, удаляем его
      const newSelected = { ...selectedRelatedProducts };
      delete newSelected[relatedProduct.id];
      setSelectedRelatedProducts(newSelected);
    } else {
      // Добавляем продукт
      setSelectedRelatedProducts({
        ...selectedRelatedProducts,
        [relatedProduct.id]: {
          name: relatedProduct.name,
          price: parsePrice(relatedProduct.price),
          id: relatedProduct.id,
        },
      });
    }
  };

  const calculatorBaseLicense = productLicenses[0] || null;

  const refreshLicenseCartState = async () => {
    if (!productId) return;

    try {
      let items = [];
      if (isAuthenticated) {
        const cartItems = await cartAPI.getCart();
        items = Array.isArray(cartItems) ? cartItems : [];
      } else {
        items = getPendingCartItems()
          .filter((row) => String(row.productId) === String(productId))
          .map((row) => ({
            product_id: row.productId,
            calculator_data: row.calculatorData,
          }));
      }
      setLicenseCartState(resolveLicenseKeysInCart(items, productId));
    } catch {
      setLicenseCartState({ keys: new Set(), legacyProductInCart: false });
    }
  };

  const handleAddPricedLicenseToCart = async (licenseIndex) => {
    if (!product) return;

    const license = productLicenses[licenseIndex];
    if (!license || !licenseHasNumericPrice(license)) return;

    const licenseKey = getLicenseKey(license, licenseIndex);
    const calculatorDataToSave = buildLicenseCalculatorData(product, license, licenseIndex);

    if (!isAuthenticated) {
      savePendingCartItem(product.id, calculatorDataToSave);
      dispatchCartUpdate();
      navigate('/login', { state: { from: CART_LOGIN_RETURN } });
      return;
    }

    setAddingLicenseKey(licenseKey);
    try {
      await cartAPI.addToCart(product.id, calculatorDataToSave);
      dispatchCartUpdate();
      await refreshLicenseCartState();
      toast({
        title: 'Товар добавлен в корзину',
        description: 'Оформите заказ в личном кабинете',
      });
    } catch (error) {
      console.error('Error adding license to cart:', error);
      toast({
        title: 'Ошибка',
        description: error.message || 'Не удалось добавить товар в корзину',
        variant: 'destructive',
      });
    } finally {
      setAddingLicenseKey(null);
    }
  };

  const handleLicenseButtonClick = (licenseIndex) => {
    const license = productLicenses[licenseIndex];
    if (!license) return;

    if (!licenseHasNumericPrice(license)) {
      setPriceRequestLicenseIndex(licenseIndex);
      setPriceRequestOpen(true);
      return;
    }

    if (isLicenseInCart(licenseCartState, license, licenseIndex)) {
      navigateToCart(navigate);
      return;
    }

    handleAddPricedLicenseToCart(licenseIndex);
  };

  // Данные о вендоре из calculator_config или дефолтные
  const vendorInfo = useMemo(() => {
    if (!product || !product.calculator_config) {
      return DEFAULT_VENDOR_INFO;
    }

    const config = product.calculator_config;
    const vi = config.vendor_info || {};

    const defaultAdditionalServiceById = new Map(
      (DEFAULT_VENDOR_INFO.additionalServices || []).map((s) => [s.id, s]),
    );

    const additionalServices = config.additionalServices && config.additionalServices.length > 0
      ? config.additionalServices.map((service, idx) => ({
          id: service.id || `service-${idx}`,
          name: service.name || '',
          price: typeof service.price === 'number' ? service.price : parsePrice(service.price || '0'),
          // Если частота не передана из БД, берём дефолт по id.
          isMonthly:
            service.isMonthly !== undefined
              ? Boolean(service.isMonthly)
              : Boolean(defaultAdditionalServiceById.get(service.id)?.isMonthly),
        }))
      : DEFAULT_VENDOR_INFO.additionalServices.map(service => ({
          // Дефолтные услуги должны сохранять свою частоту (ежемесячно/разово)
          ...service,
          isMonthly: Boolean(service.isMonthly),
        }));
    
    const basePricePerUser = config.basePricePerUser || parsePrice(product.price);
    const hasBasePrice = basePricePerUser > 0;
    
    return {
      materials: normalizeProductMaterials(vi.materials),
      deliveryTerms: vi.deliveryTerms && vi.deliveryTerms.length > 0 ? vi.deliveryTerms : DEFAULT_VENDOR_INFO.deliveryTerms,
      additionalServices: additionalServices,
      basePricePerUser: basePricePerUser,
      showUserCount: (config.showUserCount !== undefined ? config.showUserCount : true) && hasBasePrice,
      hasBasePrice: hasBasePrice,
    };
  }, [product]);

  // Загрузка продукта при монтировании или изменении id
  useEffect(() => {
    const loadProduct = async () => {
      if (!productId) return;
      
      try {
        setLoading(true);
        setError(null);
        const productData = await productsAPI.getProduct(productId);
        setProduct(productData);
        setLoading(false);

        productsAPI
          .getProducts()
          .then((allProducts) => {
            const related = ensureArray(allProducts)
              .filter(
                (p) =>
                  p.id !== productData.id &&
                  Array.isArray(p.situations) &&
                  Array.isArray(productData.situations) &&
                  p.situations.some((s) => productData.situations.includes(s)),
              )
              .slice(0, 3);
            setRelatedProducts(related);
          })
          .catch(() => setRelatedProducts([]));
      } catch (err) {
        console.error('Error loading product:', err);
        setError(err.message || 'Ошибка при загрузке продукта');
        if (err.message === 'Продукт не найден') {
          navigate('/404', { replace: true });
        }
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [productId, navigate]);

  useEffect(() => {
    const vendorName = product?.vendor?.trim();
    if (!vendorName) {
      setCatalogVendor(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = ensureArray(await vendorsAPI.getVendors());
        if (cancelled) return;
        const found =
          list.find(
            (v) => (v.name || '').trim().toLowerCase() === vendorName.toLowerCase(),
          ) ||
          (vendorSlug
            ? list.find((v) => (v.slug || '').trim().toLowerCase() === vendorSlug.toLowerCase())
            : null);
        if (found) {
          setCatalogVendor(found);
          return;
        }
        if (vendorSlug) {
          setCatalogVendor({ name: vendorName, slug: vendorSlug });
        } else {
          setCatalogVendor(null);
        }
      } catch {
        if (!cancelled) {
          setCatalogVendor(vendorSlug ? { name: vendorName, slug: vendorSlug } : null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [product?.vendor, vendorSlug]);

  const featuredNewsSlug = product?.calculator_config?.featuredNewsSlug || '';

  useEffect(() => {
    if (!featuredNewsSlug) {
      setFeaturedPublication(null);
      return undefined;
    }
    if (resolveFeaturedNewsFromBlog(featuredNewsSlug)) {
      setFeaturedPublication(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const publication = await newsAPI.getPublication(featuredNewsSlug);
        if (!cancelled) setFeaturedPublication(publication);
      } catch {
        if (!cancelled) setFeaturedPublication(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [featuredNewsSlug, product?.id]);

  const featuredNews = useMemo(
    () => resolveFeaturedProductNews(featuredNewsSlug, featuredPublication),
    [featuredNewsSlug, featuredPublication],
  );
  const vendorCertificateUrl = (catalogVendor?.certificate || '').trim();
  const showNewsSidebar = Boolean(vendorCertificateUrl || featuredNews);

  useEffect(() => {
    if (!productId) return;

    let cancelled = false;

    const syncLicenseCart = async () => {
      try {
        let items = [];
        if (isAuthenticated) {
          const cartItems = await cartAPI.getCart();
          items = Array.isArray(cartItems) ? cartItems : [];
        } else {
          const sessionId = getGuestSessionId();
          const cart = await cartAPI.getGuestCart(sessionId);
          items = cart?.items || [];
        }
        if (!cancelled) {
          setLicenseCartState(resolveLicenseKeysInCart(items, productId));
        }
      } catch {
        if (!cancelled) {
          setLicenseCartState({ keys: new Set(), legacyProductInCart: false });
        }
      }
    };

    syncLicenseCart();
    const onCartUpdated = () => syncLicenseCart();
    window.addEventListener('cart-updated', onCartUpdated);
    return () => {
      cancelled = true;
      window.removeEventListener('cart-updated', onCartUpdated);
    };
  }, [productId, isAuthenticated]);

  const productSeoStub = useMemo(() => {
    if (!vendorSlug || !solutionSlug) return null;
    return buildProductSeo(
      { id: solutionSlug },
      catalogVendor || { name: humanizeSlug(vendorSlug) || vendorSlug, slug: vendorSlug },
      { vendorSlug, solutionSlug },
    );
  }, [vendorSlug, solutionSlug, catalogVendor]);

  // SEO данные для страницы продукта (оптимизация под средне и низкочастотные запросы)
  const seoData = useMemo(() => {
    if (!product) {
      if (!productSeoStub) return null;
      return {
        title: productSeoStub.title,
        description: productSeoStub.description,
        keywords: productSeoStub.keywords,
        heroSubtitle: productSeoStub.heroSubtitle,
        url: productSeoStub.canonical,
        image: '/og-image.jpg',
        type: 'product',
        structuredData: null,
        canonical: productSeoStub.canonical,
      };
    }

    const vendorForSeo =
      catalogVendor ||
      (product?.vendor
        ? { name: product.vendor, slug: vendorSlug || undefined }
        : null);

    const productSeo = buildProductSeo(product, vendorForSeo, {
      vendorSlug,
      solutionSlug,
      catalogVendor: vendorForSeo,
    });
    const productUrl = productSeo.canonical;

    // Генерация FAQ для AI-решений
    const generateFAQ = () => {
      // Используем из БД если есть
      if (product.ai_faq && product.ai_faq.length > 0) {
        return product.ai_faq.map(faq => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.a
          }
        }));
      }
      
      if (product.category !== 'ai') return null;
      
      if (product.id === 'ai-agent') {
        return [
          {
            '@type': 'Question',
            name: 'Что такое AI-оператор для поиска информации?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'AI-оператор — это интеллектуальная система на базе искусственного интеллекта, которая автоматизирует поиск информации в корпоративных системах. Она работает в 10 раз эффективнее ручного поиска, интегрируется с дисками, файлами, сайтами и внутренними системами компании.'
            }
          },
          {
            '@type': 'Question',
            name: 'Сколько времени занимает внедрение AI-оператора?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Внедрение AI-оператора для поиска информации занимает от 1 до 3 рабочих дней. Мы проводим интеграцию с вашими системами, настраиваем поиск по корпоративным ресурсам и обучаем сотрудников работе с системой.'
            }
          },
          {
            '@type': 'Question',
            name: 'С какими системами интегрируется AI-оператор?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'AI-оператор интегрируется с облачными дисками (Яндекс.Диск, Google Drive), корпоративными файловыми хранилищами, сайтами компании, CRM-системами и другими внутренними системами. Поддерживается интеграция с большинством популярных форматов файлов.'
            }
          }
        ];
      } else if (product.id === 'knowledge-base') {
        return [
          {
            '@type': 'Question',
            name: 'Что такое корпоративная база знаний с AI-поиском?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Корпоративная база знаний с AI-поиском — это интеллектуальная система управления документами компании. Она использует искусственный интеллект для семантического поиска по документам, обеспечивая быстрые и точные ответы на вопросы сотрудников из корпоративной документации.'
            }
          },
          {
            '@type': 'Question',
            name: 'Какие форматы файлов поддерживает база знаний?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'База знаний поддерживает большинство популярных форматов: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, HTML, а также файлы из облачных хранилищ. Система автоматически индексирует документы и делает их доступными для поиска.'
            }
          },
          {
            '@type': 'Question',
            name: 'Можно ли интегрировать базу знаний с CRM и другими системами?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Да, база знаний интегрируется с популярными CRM-системами (Битрикс24, amoCRM, 1С), облачными дисками, корпоративными сайтами и другими внутренними системами. Интеграция позволяет использовать единую точку доступа к информации из разных источников.'
            }
          }
        ];
      } else if (product.id === 'crm-integration') {
        return [
          {
            '@type': 'Question',
            name: 'Что такое Vibe-coding методология разработки?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Vibe-coding — это уникальная методология разработки, которая сочетает классические подходы к созданию корпоративного ПО с возможностями искусственного интеллекта. Позволяет создавать MVP за дни вместо месяцев, сохраняя при этом качество и надежность решений.'
            }
          },
          {
            '@type': 'Question',
            name: 'Сколько времени занимает разработка AI-решения под заказ?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Срок разработки зависит от сложности проекта. Простые AI-решения для автоматизации процессов создаются за 5-10 рабочих дней. Более сложные системы с глубокой интеграцией могут занять 2-4 недели. Мы всегда предоставляем детальный план работ с указанием сроков.'
            }
          },
          {
            '@type': 'Question',
            name: 'Предоставляется ли поддержка после внедрения?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Да, мы предоставляем полную техническую поддержку после внедрения решения. Включает исправление ошибок, обновления системы, консультации по использованию и развитие функционала по запросу клиента.'
            }
          }
        ];
      }
      return null;
    };

    const faqData = generateFAQ();

    const baseUrl = process.env.REACT_APP_BASE_URL || 'https://antologia.tech';
    const rawVendorLogo =
      (catalogVendor?.logo || '').trim() ||
      (product.calculator_config?.vendor_info?.logo || '').trim();
    const absoluteVendorImage = rawVendorLogo
      ? rawVendorLogo.startsWith('http://') || rawVendorLogo.startsWith('https://')
        ? rawVendorLogo
        : `${baseUrl}${rawVendorLogo.startsWith('/') ? '' : '/'}${rawVendorLogo}`
      : `${baseUrl}/og-image.jpg`;

    // Структурированные данные для продукта
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      '@id': productUrl,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': productUrl,
      },
      name: product.name,
      description: productSeo.description,
      image: absoluteVendorImage,
      brand: {
        '@type': 'Brand',
        name: 'Антология'
      },
      offers: {
        '@type': 'Offer',
        price: product.price || '0',
        priceCurrency: 'RUB',
        availability: 'https://schema.org/InStock',
        url: productUrl
      },
      aggregateRating: product.category === 'ai' ? {
        '@type': 'AggregateRating',
        ratingValue: '4.8',
        reviewCount: '45'
      } : undefined,
      category: product.category === 'ai' ? 'AI-решения' : product.category,
      // AI SEO данные
      ...(product.ai_summary && { additionalDescription: product.ai_summary }),
      ...(product.ai_keywords && { keywords: product.ai_keywords }),
      ...(product.ai_features && product.ai_features.length > 0 && {
        additionalProperty: [
          ...product.features.map(feature => ({
            '@type': 'PropertyValue',
            name: 'Возможность',
            value: feature
          })),
          ...product.ai_features.map(feature => ({
            '@type': 'PropertyValue',
            name: 'AI-возможность',
            value: feature
          }))
        ]
      }),
      ...(product.ai_use_cases && product.ai_use_cases.length > 0 && product.category === 'ai' && {
        potentialAction: product.ai_use_cases.map(useCase => ({
          '@type': 'Action',
          name: useCase
        }))
      })
    };

    // Удаляем undefined поля
    Object.keys(structuredData).forEach(key => 
      structuredData[key] === undefined && delete structuredData[key]
    );

    // Добавляем FAQPage schema если есть FAQ
    const allStructuredData = [structuredData];
    if (faqData && faqData.length > 0) {
      allStructuredData.push({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqData
      });
    }

    return {
      title: productSeo.title,
      description: productSeo.description,
      keywords: productSeo.keywords,
      heroSubtitle: productSeo.heroSubtitle,
      url: productUrl,
      image: rawVendorLogo || '/og-image.jpg',
      type: 'product',
      structuredData: allStructuredData,
      canonical: productUrl,
    };
  }, [product, catalogVendor, vendorSlug, solutionSlug, productSeoStub]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        {seoData && (
          <SEOHead
            title={seoData.title}
            description={seoData.description}
            keywords={seoData.keywords}
            canonical={seoData.canonical}
          />
        )}
        <Header />
        <main className="flex-1 pt-16 flex items-center justify-center">
          <p className="text-muted-foreground">Загрузка продукта...</p>
        </main>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen flex flex-col">
        {seoData && (
          <SEOHead
            title={seoData.title}
            description={seoData.description}
            keywords={seoData.keywords}
            canonical={seoData.canonical}
          />
        )}
        <Header />
        <main className="flex-1 pt-16 flex items-center justify-center">
          <div className="text-center">
            <p className="text-destructive mb-4">{error || 'Продукт не найден'}</p>
            <Button onClick={() => navigate('/')}>Вернуться на главную</Button>
          </div>
        </main>
      </div>
    );
  }

  const rawVendorLogo = (catalogVendor?.logo || resolveVendorLogo(product) || '').trim();
  const vendorLogoSrc = isProductIconImageUrl(rawVendorLogo) ? rawVendorLogo : null;

  const benefits = [
    {
      icon: Zap,
      title: 'Быстрый запуск',
      description: 'Интеграция за 1-3 дня',
    },
    {
      icon: Shield,
      title: 'Безопасность',
      description: 'Соответствие 152-ФЗ',
    },
    {
      icon: Clock,
      title: 'Поддержка',
      description: 'Поддержка LLM и API',
    },
    {
      icon: Users,
      title: 'Обучение',
      description: 'Поможем настроить',
    },
  ];

  const { mainHtml: heroDescriptionHtml } = splitProductDescription(
    product.full_description ||
      product.fullDescription ||
      product.short_description ||
      product.shortDescription ||
      product.hero_description ||
      '',
  );

  const vendorCatalogHref = (() => {
    const back = getCatalogBack(location.state);
    if (back) return back;
    if (!catalogVendor?.slug) return '/solutions';
    const params = new URLSearchParams(location.search);
    return catalogVendorPath(catalogVendor.slug, {
      categoryId: params.get('category') || undefined,
      sectionName: params.get('section') || undefined,
      situationId: params.get('situation') || undefined,
    });
  })();

  const vendorDisplayName = (catalogVendor?.name || product?.vendor || '').trim();
  const vendorPageHref =
    catalogVendor?.slug
      ? solutionsPathForVendorSlug(catalogVendor.slug)
      : vendorSlug
        ? solutionsPathForVendorSlug(vendorSlug)
        : null;

  const handleSolutionBack = () => {
    const current = currentCatalogHref(location);
    const target = getCatalogBack(location.state) ?? inferCatalogParentHref(current, []);
    if (!target || target === current) {
      navigate('/solutions');
      return;
    }
    const parent = inferCatalogParentHref(target, []);
    navigate(target, {
      replace: true,
      state: parent && parent !== target ? withCatalogBack({}, parent) : undefined,
    });
  };

  const showSolutionBack =
    !isLegacyProductPath && Boolean(getCatalogBack(location.state) || vendorSlug);

  return (
    <div className="min-h-screen flex flex-col">
      {seoData && (
        <SEOHead
          title={seoData.title}
          description={seoData.description}
          keywords={seoData.keywords}
          url={seoData.url}
          image={seoData.image}
          type={seoData.type}
          structuredData={seoData.structuredData}
          canonical={seoData.canonical}
          noindex={isLegacyProductPath}
        />
      )}
      <Header />

      <main className="flex-1 pt-16" data-product-seo-ready="true">
        {/* Breadcrumb с структурированными данными */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {showSolutionBack && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="gap-2 h-9 px-2 mb-3 text-muted-foreground hover:text-foreground"
              onClick={handleSolutionBack}
            >
              <ArrowLeft className="w-4 h-4 shrink-0" />
              Назад
            </Button>
          )}
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Главная</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/solutions">Каталог решений</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              {product.category && (
                <>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link to={`/solutions?category=${product.category}`}>
                        {product.category === 'ai' ? 'AI-решения' : 
                         product.category === 'communications' || product.category === 'ofis_i_kommunikatsii' ? 'Офис и коммуникации' :
                         product.category === 'omnichannel' ? 'Омниканальность' :
                         product.category === 'analytics' ? 'Аналитика' :
                         product.category === 'integration' ? 'Интеграция' :
                         product.category}
                      </Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                </>
              )}
              {product.vendor && (
                <>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link to={vendorCatalogHref}>
                        {product.vendor}
                      </Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                </>
              )}
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{product.name}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          
          {/* Структурированные данные для BreadcrumbList */}
          {seoData && (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  '@context': 'https://schema.org',
                  '@type': 'BreadcrumbList',
                  itemListElement: [
                    {
                      '@type': 'ListItem',
                      position: 1,
                      name: 'Главная',
                      item: 'https://antologia.tech'
                    },
                    {
                      '@type': 'ListItem',
                      position: 2,
                      name: 'Каталог решений',
                      item: 'https://antologia.tech'
                    },
                    ...(product.category === 'ai' ? [{
                      '@type': 'ListItem',
                      position: 3,
                      name: 'AI-решения',
                      item: 'https://antologia.tech/?category=ai'
                    }] : []),
                    {
                      '@type': 'ListItem',
                      position: product.category === 'ai' ? 4 : 3,
                      name: product.name,
                      item: seoData.url
                    }
                  ]
                })
              }}
            />
          )}
        </div>

        {/* Hero Section */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-accent/5 via-transparent to-transparent" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative">
            <div className={`grid gap-8 items-start ${showNewsSidebar ? 'lg:grid-cols-[minmax(0,1fr)_320px]' : ''}`}>
              {/* Content — карточка на белом фоне, как блок «Возможности» справа */}
              <Card className="animate-slide-up bg-card border-border/50 shadow-sm">
                <CardContent className="p-8">
                <div className="mb-6">
                  <Badge variant="secondary" className="text-sm">
                    {(product.category === 'communications' || product.category === 'ofis_i_kommunikatsii') && 'Офис и коммуникации'}
                    {product.category === 'omnichannel' && 'Омниканальность'}
                    {product.category === 'ai' && 'AI-решение'}
                    {product.category === 'analytics' && 'Аналитика'}
                    {product.category === 'integration' && 'Интеграция'}
                  </Badge>
                </div>

                <h1 className="text-4xl md:text-5xl font-bold mb-6">
                  {product.hero_title || (
                    product.category === 'ai' && product.id === 'ai-agent' 
                      ? 'AI-оператор для поиска информации в бизнесе'
                      : product.category === 'ai' && product.id === 'knowledge-base'
                      ? 'Корпоративная база знаний с AI-поиском'
                      : product.category === 'ai' && product.id === 'crm-integration'
                      ? 'Заказная разработка AI-решений для бизнеса'
                      : product.category === 'ai'
                      ? 'AI-решение для бизнеса с внедрением и поддержкой'
                      : product.name
                  )}
                </h1>
                {vendorDisplayName ? (
                  <p className="mb-2 text-base text-muted-foreground">
                    {seoData?.heroSubtitle || 'Оплата в рублях для юрлиц.'}
                  </p>
                ) : null}
                {vendorDisplayName ? (
                  <p className="mb-4 text-sm text-muted-foreground">
                    Вендор:{' '}
                    {vendorPageHref ? (
                      <Link
                        to={vendorPageHref}
                        className="font-medium text-accent hover:underline"
                      >
                        {vendorDisplayName}
                      </Link>
                    ) : (
                      <span className="font-medium text-foreground">{vendorDisplayName}</span>
                    )}
                  </p>
                ) : null}
                {heroDescriptionHtml ? (
                  <>
                    <div
                      className="mb-4 prose prose-lg max-w-none text-muted-foreground dark:prose-invert prose-p:leading-relaxed prose-headings:text-foreground [&_a]:text-accent [&_ul]:my-3 [&_ol]:my-3"
                      dangerouslySetInnerHTML={{ __html: heroDescriptionHtml }}
                    />
                    <ProductVendorTermsNotice />
                  </>
                ) : null}

                {vendorInfo.materials && vendorInfo.materials.length > 0 && (
                  <div className="mb-8">
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Материалы о продукте
                    </h3>
                    <div className="space-y-2">
                      {Array.isArray(vendorInfo.materials) && vendorInfo.materials.map((material, idx) => (
                        <a
                          key={idx}
                          href={material.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm text-accent hover:underline"
                        >
                          <Download className="w-4 h-4" />
                          {material.name}
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-2 mb-8">
                  {product.tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="text-xs border-0">
                      {tag}
                    </Badge>
                  ))}
                </div>

                <div className="mt-6 pt-6 border-t border-border/50">
                  <h3 className="text-lg font-bold text-foreground mb-4">
                    Запишитесь на консультацию с экспертом!
                  </h3>
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-accent/20">
                      <img
                        src="/images/founder.png"
                        alt="Сергей Успенский"
                        className="h-full w-full object-cover"
                      />
                    </div>
                    <div>
                      <p className="font-semibold">Сергей Успенский</p>
                      <p className="text-sm text-muted-foreground">Эксперт по IT-решениям</p>
                    </div>
                    <Button
                      onClick={() => {
                        trackExpertVendorClick();
                        setExpertContactOpen(true);
                      }}
                    >
                      Связаться
                    </Button>
                  </div>
                </div>

                <div className="mt-8 pt-8 border-t border-border/50">
                  <div className="flex flex-col lg:flex-row lg:items-stretch gap-8">
                    <div className="flex-1 min-w-0 space-y-3">
                    <h3 className="text-lg font-semibold">Лицензии</h3>
                    {productLicenses.length > 0 ? (
                      <div className="space-y-4">
                        {productLicenses.map((license, idx) => {
                          const licenseKey = getLicenseKey(license, idx);
                          const hasPrice = licenseHasNumericPrice(license);
                          const inCart = hasPrice && isLicenseInCart(licenseCartState, license, idx);
                          const baseDeliveryTerms = getLicenseDeliveryTerms(
                            license,
                            vendorInfo.deliveryTerms,
                          );
                          const deliveryTerms = product?.sku
                            ? [{ condition: 'Артикул', value: product.sku }, ...baseDeliveryTerms]
                            : baseDeliveryTerms;
                          const isAdding = addingLicenseKey === licenseKey;

                          return (
                            <div
                              key={licenseKey}
                              className="rounded-lg border border-border/50 bg-muted/20 p-4"
                            >
                              <div className="grid gap-4 lg:grid-cols-[1fr_minmax(200px,280px)]">
                                <div className="space-y-3 text-sm">
                                  <div className="font-semibold text-base">{license.name}</div>
                                  {license.description && (
                                    <p className="text-muted-foreground">{license.description}</p>
                                  )}
                                  <div className="text-lg font-semibold text-foreground">
                                    {formatLicensePrice(license.price)}
                                  </div>
                                  <Button
                                    type="button"
                                    variant={inCart ? 'destructive' : 'default'}
                                    className={
                                      inCart
                                        ? 'w-full sm:w-auto'
                                        : 'w-full sm:w-auto bg-accent hover:bg-accent/90 text-accent-foreground'
                                    }
                                    size="default"
                                    disabled={hasPrice && isAdding && !inCart}
                                    onClick={() => handleLicenseButtonClick(idx)}
                                  >
                                    {!hasPrice
                                      ? 'Запросить цену'
                                      : isAdding && !inCart
                                        ? 'Добавление...'
                                        : inCart
                                          ? 'Перейти в корзину'
                                          : 'В корзину'}
                                  </Button>
                                </div>

                                {deliveryTerms.length > 0 && (
                                  <div className="border-t lg:border-t-0 lg:border-l border-border/50 pt-4 lg:pt-0 lg:pl-4">
                                    <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                                      Условия поставки
                                    </h4>
                                    <Table>
                                      <TableBody>
                                        {deliveryTerms.map((term, termIdx) => (
                                          <TableRow key={`${licenseKey}-${termIdx}`}>
                                            <TableCell className="py-1.5 pl-0 text-xs font-medium align-top">
                                              {term.condition}
                                            </TableCell>
                                            <TableCell className="py-1.5 pr-0 text-xs text-right text-muted-foreground align-top">
                                              {term.value}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">Лицензии пока не добавлены у вендора.</p>
                    )}
                  </div>
                    {vendorLogoSrc && (
                      <div className="flex w-full lg:w-48 xl:w-56 flex-shrink-0 items-center justify-center">
                        <div className="w-32 h-32 lg:w-40 lg:h-40 rounded-xl bg-accent/10 flex items-center justify-center overflow-hidden p-4">
                          <img
                            src={vendorLogoSrc}
                            alt={vendorDisplayName || 'Логотип вендора'}
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = DEFAULT_PRODUCT_ICON_PATH;
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  {product.landing_url && (
                    <a 
                      href={product.landing_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 mt-4 text-sm text-accent hover:underline"
                    >
                      Открыть лендинг решения
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>
                </CardContent>
              </Card>

              {showNewsSidebar && (
                <ProductNewsSidebar
                  certificateUrl={vendorCertificateUrl}
                  vendorName={vendorDisplayName || catalogVendor?.name}
                  featuredNews={featuredNews}
                />
              )}

            </div>
          </div>
        </section>

        {/* Калькулятор */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t border-border/50">
          <Card className="bg-card/50 border-border/50">
            <CardContent className="p-6">
              <h2 className="text-2xl font-bold mb-6">
                {product.category === 'ai'
                  ? 'Стоимость внедрения для вашего бизнеса'
                  : 'Услуги'
                }
              </h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                {/* Левая часть - дополнительные услуги */}
                <div className="space-y-6">
                  {/* Дополнительные услуги */}
                  <div>
                    <div className="space-y-2">
                      {Array.isArray(vendorInfo.additionalServices) && vendorInfo.additionalServices.map((service) => (
                        <div
                          key={service.id}
                          onClick={() =>
                            setSelectedServices({
                              ...selectedServices,
                              [service.id]: !selectedServices[service.id],
                            })
                          }
                          className="flex items-center justify-between p-3 border border-border/50 rounded-lg hover:border-accent/50 transition-colors cursor-pointer"
                        >
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={selectedServices[service.id] || false}
                              onChange={(e) => {
                                e.stopPropagation();
                                setSelectedServices({
                                  ...selectedServices,
                                  [service.id]: e.target.checked,
                                });
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="w-4 h-4 text-accent pointer-events-auto"
                            />
                            <span className="text-sm">{service.name}</span>
                            {service.description && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedServiceForPopup(service);
                                }}
                                className="text-xs text-accent hover:underline ml-1 pointer-events-auto"
                              >
                                подробнее
                              </button>
                            )}
                            <Badge variant={service.isMonthly ? "default" : "outline"} className="text-[10px] px-1.5 py-0 h-4 leading-4">
                              {service.isMonthly ? "Ежемесячно" : "Разово"}
                            </Badge>
                          </div>
                          <span className="text-sm font-medium">
                            {service.price > 0 ? `${service.price.toLocaleString('ru-RU')} ₽${service.isMonthly ? '/мес' : ''}` : 'по запросу'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Правая часть - итоговая стоимость */}
                <div className="flex flex-col justify-between">
                  <div className="space-y-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Базовый план</span>
                      <span className="font-medium">
                        {formatLicensePrice(calculatorBaseLicense?.price)}
                      </span>
                    </div>
                    
                    {(Object.keys(selectedServices).filter(key => selectedServices[key]).length > 0 || Object.keys(selectedRelatedProducts).length > 0) && (
<div className="border-t border-border/50 pt-4">
                          {Object.keys(selectedServices).filter(key => selectedServices[key]).length > 0 && (
                            <>
                              <div className="text-sm font-medium mb-2">Дополнительные услуги:</div>
                              {Array.isArray(vendorInfo.additionalServices) && vendorInfo.additionalServices
                                .filter(service => selectedServices[service.id])
                                .map(service => (
                                <div key={service.id} className="flex justify-between text-sm mb-2">
                                  <span className="text-muted-foreground">
                                    {service.name}
                                  </span>
                                  <span>{service.price > 0 ? `${service.price.toLocaleString('ru-RU')} ₽${service.isMonthly ? '/мес' : ''}` : 'по запросу'}</span>
                                </div>
                              ))
                            }
                          </>
                        )}
                        {Object.keys(selectedRelatedProducts).length > 0 && (
                          <>
                            {Object.keys(selectedServices).filter(key => selectedServices[key]).length > 0 && (
                              <div className="text-sm font-medium mb-2 mt-3">Сопутствующие решения:</div>
                            )}
                            {Object.keys(selectedServices).filter(key => selectedServices[key]).length === 0 && (
                              <div className="text-sm font-medium mb-2">Сопутствующие решения:</div>
                            )}
                            {Object.values(selectedRelatedProducts).map((product) => (
                              <div key={product.id} className="flex justify-between text-sm mb-2">
                                <span className="text-muted-foreground">{product.name}</span>
                                <span>{product.price ? product.price.toLocaleString('ru-RU') + ' ₽' : 'по запросу'}</span>
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="border-t border-border/50 pt-4 mt-auto">
                    {(() => {
                      const basePrice = getLicenseNumericPrice(calculatorBaseLicense);
                      const monthlyServices = (Array.isArray(vendorInfo.additionalServices) ? vendorInfo.additionalServices : [])
                        .filter(service => selectedServices[service.id] && service.isMonthly && service.price > 0);
                      const oneTimeServices = (Array.isArray(vendorInfo.additionalServices) ? vendorInfo.additionalServices : [])
                        .filter(service => selectedServices[service.id] && !service.isMonthly && service.price > 0);
                      const monthlyServicesTotal = monthlyServices.reduce((sum, service) => sum + service.price, 0);
                      const oneTimeServicesTotal = oneTimeServices.reduce((sum, service) => sum + service.price, 0);
                      const relatedTotal = Object.values(selectedRelatedProducts).reduce((sum, product) => sum + (product.price || 0), 0);
                      const monthlyTotal = basePrice > 0 ? basePrice + monthlyServicesTotal : monthlyServicesTotal;
                      const oneTimeTotal = oneTimeServicesTotal + relatedTotal;
                      const totalSum = monthlyTotal + oneTimeTotal;
                      
                      return (
                        <>
                          {totalSum > 0 && (
                            <div className="flex justify-between items-center mb-4">
                              <span className="text-lg font-bold">Итого:</span>
                              <span className="text-2xl font-bold text-accent">
                                {totalSum.toLocaleString('ru-RU')} ₽
                              </span>
                            </div>
                          )}
                          {totalSum === 0 && (
                            <div className="flex justify-between items-center mb-4">
                              <span className="text-lg font-bold">Итого:</span>
                              <span className="text-2xl font-bold text-accent">
                                по запросу
                              </span>
                            </div>
                          )}
                        </>
                      );
                    })()}
<Button
                      onClick={async () => {
                        // Расчёт данных калькулятора
                        const basePrice = getLicenseNumericPrice(calculatorBaseLicense);
                        const monthlyServices = (Array.isArray(vendorInfo.additionalServices) ? vendorInfo.additionalServices : [])
                          .filter(service => selectedServices[service.id] && service.isMonthly && service.price > 0);
                        const oneTimeServices = (Array.isArray(vendorInfo.additionalServices) ? vendorInfo.additionalServices : [])
                          .filter(service => selectedServices[service.id] && !service.isMonthly && service.price > 0);
                        const monthlyServicesPrice = monthlyServices.reduce((sum, service) => sum + service.price, 0);
                        const oneTimeServicesPrice = oneTimeServices.reduce((sum, service) => sum + service.price, 0);
                        const relatedTotal = Object.values(selectedRelatedProducts).reduce((sum, product) => sum + (product.price || 0), 0);
                          const monthlyPriceCalc = basePrice > 0 ? basePrice + monthlyServicesPrice : monthlyServicesPrice;
                        const oneTimePriceCalc = oneTimeServicesPrice + relatedTotal;
                        const totalPriceCalc = monthlyPriceCalc + oneTimeServicesPrice;
                        
                        const selectedServicesList = [
                          ...(Array.isArray(vendorInfo.additionalServices) ? vendorInfo.additionalServices : [])
                            .filter(service => selectedServices[service.id])
                            .map(service => ({ name: service.name, price: service.price, isMonthly: service.isMonthly })),
                          ...Object.values(selectedRelatedProducts).map(product => ({ name: product.name, price: product.price, isMonthly: false }))
                        ];

                        const calculatorDataToSave = {
                          userCount: 1,
                          selectedServices: selectedServicesList,
                          totalPrice: totalPriceCalc || null,
                          basePrice: basePrice || null,
                          monthlyPrice: monthlyPriceCalc || null,
                          oneTimePrice: oneTimePriceCalc || null,
                          priceDisplay: product.price || 'по запросу'
                        };

                        if (!isAuthenticated) {
                          try {
                            const sessionId = getGuestSessionId();
                            await cartAPI.addToGuestCart(product.id, calculatorDataToSave, sessionId);
                            toast({
                              title: "Товар добавлен в корзину",
                              description: "Перейдите в корзину для оформления заказа",
                            });
                          } catch (error) {
                            console.error('Error adding to guest cart:', error);
                            toast({
                              title: "Ошибка",
                              description: error.message || "Не удалось добавить товар в корзину",
                              variant: "destructive",
                            });
                          }
                          return;
                        }

                        setAddingToCart(true);
                        try {
                          await cartAPI.addToCart(product.id, calculatorDataToSave);
                          
                          // Сохраняем в localStorage для неавторизованных
                          savePendingCartItem(product.id, calculatorDataToSave);
                          dispatchCartUpdate();
                          
                          toast({
                            title: "Товар добавлен в корзину",
                            description: "Вы можете оформить заказ или продолжить покупки",
                            action: (
                              <Button 
                                size="sm" 
                                onClick={() => navigateToCart(navigate)}
                                className="bg-accent hover:bg-accent/90"
                              >
                                Оформить
                              </Button>
                            ),
                          });
                          
                          // Опционально: перенаправить в корзину
                          // navigate('/dashboard?tab=cart');
                        } catch (error) {
                          console.error('Error adding to cart:', error);
                          toast({
                            title: "Ошибка",
                            description: error.message || "Не удалось добавить товар в корзину",
                            variant: "destructive",
                          });
                        } finally {
                          setAddingToCart(false);
                        }
                      }}
                      className="w-full mt-4 bg-accent hover:bg-accent/90 text-accent-foreground font-medium"
                      size="lg"
                      disabled={addingToCart}
                    >
                      {addingToCart ? 'Добавление...' : 'Добавить в корзину'}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t border-border/50">
            <h2 className="text-2xl font-bold mb-8">
              {product.category === 'ai'
                ? 'Дополнительные AI-решения для автоматизации бизнеса'
                : 'Сопутствующие решения'
              }
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedProducts.map((relatedProduct) => {
                const relatedIconSrc = getProductIconSrcForProduct(relatedProduct);
                const isAdded = selectedRelatedProducts[relatedProduct.id];
                return (
                  <div key={relatedProduct.id} className="relative">
                    <Link to={`/product/${relatedProduct.id}`}>
                      <Card className="h-full bg-card/30 border-border/30 hover:border-accent/30 transition-all cursor-pointer group">
                        <CardContent className="p-6 pb-16">
                          <div className="flex items-center gap-4 mb-4">
                            <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors overflow-hidden">
                              <img
                                src={relatedIconSrc}
                                alt=""
                                className="w-7 h-7 object-contain"
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.src = DEFAULT_PRODUCT_ICON_PATH;
                                }}
                              />
                            </div>
                            <div>
                              <h3 className="font-semibold group-hover:text-accent transition-colors">
                                {relatedProduct.name}
                              </h3>
                              <p className="text-sm text-accent">{relatedProduct.price || 'по запросу'}</p>
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {relatedProduct.short_description || relatedProduct.shortDescription}
                          </p>
                        </CardContent>
                      </Card>
                    </Link>
                    <Button
                      onClick={(e) => handleAddRelatedProduct(relatedProduct, e)}
                      variant={isAdded ? "outline" : "default"}
                      size="sm"
                      className={`absolute bottom-4 right-4 ${isAdded ? 'bg-accent/10 text-accent border-accent/30' : 'bg-accent hover:bg-accent/90 text-accent-foreground'}`}
                    >
                      {isAdded ? 'Добавлено' : 'Добавить в заказ'}
                    </Button>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Benefits Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t border-border/50">
          <h2 className="text-2xl font-bold mb-8 text-center">
            {product.category === 'ai'
              ? 'Почему выбирают Антологию для внедрения AI-решений'
              : 'Почему выбирают Антологию'
            }
          </h2>
          
          {/* Дополнительный SEO-контент для AI-решений */}
          {product.category === 'ai' && (
            <div className="mb-12 max-w-4xl mx-auto">
              <div className="prose prose-sm max-w-none text-center mb-8">
                <p className="text-muted-foreground leading-relaxed">
                  {product.ai_benefits_text ? (
                    <span dangerouslySetInnerHTML={{ __html: product.ai_benefits_text.replace(/<strong>/g, '<strong class="font-semibold">').replace(/<\/strong>/g, '</strong>') }} />
                  ) : (
                    <>
                      {product.id === 'ai-agent' && (
                        <>
                          <strong>AI-оператор для поиска информации</strong> — это современное решение для автоматизации поиска данных в корпоративных системах. 
                          Наша платформа интегрируется с дисками, файлами, сайтами и внутренними системами компании, обеспечивая 
                          <strong> в 10 раз более эффективный поиск</strong> по сравнению с ручным методом. 
                          <strong> Внедрение AI-оператора</strong> позволяет сократить время на поиск информации и повысить продуктивность сотрудников.
                        </>
                      )}
                      {product.id === 'knowledge-base' && (
                        <>
                          <strong>Корпоративная база знаний с AI-поиском</strong> — это интеллектуальная система управления документами компании. 
                          С помощью <strong>семантического поиска на базе искусственного интеллекта</strong> сотрудники получают быстрые и точные ответы 
                          на вопросы из корпоративной документации. <strong>AI-поиск по документам</strong> работает с файлами большинства форматов, 
                          интегрируется с диском, CRM и сайтами компании.
                        </>
                      )}
                      {product.id === 'crm-integration' && (
                        <>
                          <strong>Заказная разработка AI-решений</strong> для автоматизации бизнес-процессов с использованием методологии Vibe-coding. 
                          Мы создаем <strong>кастомные AI-сервисы</strong> под специфику вашего бизнеса за дни, а не месяцы. 
                          <strong> Разработка MVP с AI</strong> включает интеграцию с существующими системами и автоматизацию рутинных процессов.
                        </>
                      )}
                    </>
                  )}
                </p>
              </div>
            </div>
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, idx) => (
              <Card
                key={idx}
                className="bg-card/30 border-border/30 hover:border-accent/30 transition-colors"
              >
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="w-6 h-6 text-accent" />
                  </div>
                  <h3 className="font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Статистика - Более 100 реализованных проектов */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t border-border/50">
          <div className="text-center">
            <Card className="bg-card/30 border-border/30">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-xl bg-accent/10 flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-accent" />
                </div>
                <p className="text-2xl font-bold text-foreground mb-2">Более 100 реализованных проектов</p>
                <p className="text-sm text-muted-foreground mb-6">
                  Реализовано для среднего и крупного бизнеса. Уникальные веб-приложения, erp, hrs, веб-сервисы, корпоративные системы.
                </p>
                
                {/* Бегущая строка с логотипами */}
                <div className="mt-8">
                  <div className="overflow-hidden relative w-full">
                    <div className="flex animate-scroll-left">
                      {/* Первый набор логотипов */}
                      {[
                        { 
                          name: 'Сбербанк', 
                          logo: '/images/logos/sberbank.png' 
                        },
                        { 
                          name: 'Ozon', 
                          logo: '/images/logos/ozon.png' 
                        },
                        { 
                          name: 'VK', 
                          logo: '/images/logos/vk.png' 
                        },
                        { 
                          name: 'Pizza Hut Russia', 
                          logo: '/images/logos/pizza-hut.png' 
                        },
                        { 
                          name: 'YIT', 
                          logo: '/images/logos/yit.png' 
                        },
                        { 
                          name: 'Softline', 
                          logo: '/images/logos/softline.png' 
                        },
                        { 
                          name: 'Marvel Дистрибуция', 
                          logo: '/images/logos/marvel.jpg' 
                        },
                        { 
                          name: 'Lanit', 
                          logo: '/images/logos/lanit.png' 
                        },
                        { 
                          name: 'Jet Инфосистемы', 
                          logo: '/images/logos/jet.gif' 
                        },
                        { 
                          name: 'Газпром-Нефть', 
                          logo: '/images/logos/gazprom-neft.png' 
                        },
                        { 
                          name: 'Газпромбанк', 
                          logo: '/images/logos/gazprombank.png' 
                        },
                        { 
                          name: 'Биокад', 
                          logo: '/images/logos/biocad.png' 
                        },
                        { 
                          name: 'Takeda', 
                          logo: '/images/logos/takeda.svg' 
                        },
                        { 
                          name: 'ОМК', 
                          logo: '/images/logos/omk.jpg' 
                        },
                      ].map((company, idx) => (
                        <div key={`first-${idx}`} className="flex-shrink-0 mx-4">
                          <CompanyLogo company={company} />
                        </div>
                      ))}
                      {/* Дублируем для бесшовной анимации */}
                      {[
                        { 
                          name: 'Сбербанк', 
                          logo: '/images/logos/sberbank.png' 
                        },
                        { 
                          name: 'Ozon', 
                          logo: '/images/logos/ozon.png' 
                        },
                        { 
                          name: 'VK', 
                          logo: '/images/logos/vk.png' 
                        },
                        { 
                          name: 'Pizza Hut Russia', 
                          logo: '/images/logos/pizza-hut.png' 
                        },
                        { 
                          name: 'YIT', 
                          logo: '/images/logos/yit.png' 
                        },
                        { 
                          name: 'Softline', 
                          logo: '/images/logos/softline.png' 
                        },
                        { 
                          name: 'Marvel Дистрибуция', 
                          logo: '/images/logos/marvel.jpg' 
                        },
                        { 
                          name: 'Lanit', 
                          logo: '/images/logos/lanit.png' 
                        },
                        { 
                          name: 'Jet Инфосистемы', 
                          logo: '/images/logos/jet.gif' 
                        },
                        { 
                          name: 'Газпром-Нефть', 
                          logo: '/images/logos/gazprom-neft.png' 
                        },
                        { 
                          name: 'Газпромбанк', 
                          logo: '/images/logos/gazprombank.png' 
                        },
                        { 
                          name: 'Биокад', 
                          logo: '/images/logos/biocad.png' 
                        },
                        { 
                          name: 'Takeda', 
                          logo: '/images/logos/takeda.svg' 
                        },
                        { 
                          name: 'ОМК', 
                          logo: '/images/logos/omk.jpg' 
                        },
                      ].map((company, idx) => (
                        <div key={`second-${idx}`} className="flex-shrink-0 mx-4">
                          <CompanyLogo company={company} />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* FAQ Section */}
        {(product.ai_faq?.length > 0 || product.category === 'ai') && (
          <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t border-border/50">
            <h2 className="text-3xl font-bold mb-8 text-center">
              {product.category === 'ai' && product.id === 'ai-agent' 
                ? 'Часто задаваемые вопросы об AI-операторе'
                : product.category === 'ai' && product.id === 'knowledge-base'
                ? 'Часто задаваемые вопросы о базе знаний'
                : product.category === 'ai' && product.id === 'crm-integration'
                ? 'Часто задаваемые вопросы о разработке AI-решений'
                : `Часто задаваемые вопросы о ${product.name.toLowerCase()}`
              }
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {/* Динамические FAQ из БД */}
              {product.ai_faq?.length > 0 && product.ai_faq.map((faq, idx) => (
                <AccordionItem key={`ai-faq-${idx}`} value={`ai-faq-${idx}`}>
                  <AccordionTrigger>{faq.q}</AccordionTrigger>
                  <AccordionContent>{faq.a}</AccordionContent>
                </AccordionItem>
              ))}
              {/* Fallback FAQ для AI-решений */}
              {(!product.ai_faq || product.ai_faq.length === 0) && product.id === 'ai-agent' && (
                <>
                  <AccordionItem value="ai-agent-1">
                    <AccordionTrigger>Что такое AI-оператор для поиска информации?</AccordionTrigger>
                    <AccordionContent>
                      AI-оператор — это интеллектуальная система на базе искусственного интеллекта, которая автоматизирует поиск информации в корпоративных системах. Она работает в 10 раз эффективнее ручного поиска, интегрируется с дисками, файлами, сайтами и внутренними системами компании.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="ai-agent-2">
                    <AccordionTrigger>Сколько времени занимает внедрение AI-оператора?</AccordionTrigger>
                    <AccordionContent>
                      Внедрение AI-оператора для поиска информации занимает от 1 до 3 рабочих дней. Мы проводим интеграцию с вашими системами, настраиваем поиск по корпоративным ресурсам и обучаем сотрудников работе с системой.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="ai-agent-3">
                    <AccordionTrigger>С какими системами интегрируется AI-оператор?</AccordionTrigger>
                    <AccordionContent>
                      AI-оператор интегрируется с облачными дисками (Яндекс.Диск, Google Drive), корпоративными файловыми хранилищами, сайтами компании, CRM-системами и другими внутренними системами. Поддерживается интеграция с большинством популярных форматов файлов.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="ai-agent-4">
                    <AccordionTrigger>Как работает поиск информации с помощью AI?</AccordionTrigger>
                    <AccordionContent>
                      AI-оператор использует семантический поиск на базе нейросетей. Система понимает смысл запроса, а не просто ищет ключевые слова, что позволяет находить релевантную информацию даже при неточных формулировках. Поиск работает на русском и английском языках.
                    </AccordionContent>
                  </AccordionItem>
                </>
              )}
              {product.id === 'knowledge-base' && (
                <>
                  <AccordionItem value="kb-1">
                    <AccordionTrigger>Что такое корпоративная база знаний с AI-поиском?</AccordionTrigger>
                    <AccordionContent>
                      Корпоративная база знаний с AI-поиском — это интеллектуальная система управления документами компании. Она использует искусственный интеллект для семантического поиска по документам, обеспечивая быстрые и точные ответы на вопросы сотрудников из корпоративной документации.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="kb-2">
                    <AccordionTrigger>Какие форматы файлов поддерживает база знаний?</AccordionTrigger>
                    <AccordionContent>
                      База знаний поддерживает большинство популярных форматов: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, HTML, а также файлы из облачных хранилищ. Система автоматически индексирует документы и делает их доступными для поиска.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="kb-3">
                    <AccordionTrigger>Как обеспечивается безопасность данных в базе знаний?</AccordionTrigger>
                    <AccordionContent>
                      База знаний соответствует требованиям 152-ФЗ о защите персональных данных. Реализована система контроля доступа, разграничение прав пользователей, шифрование данных и аудит всех операций с документами.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="kb-4">
                    <AccordionTrigger>Можно ли интегрировать базу знаний с CRM и другими системами?</AccordionTrigger>
                    <AccordionContent>
                      Да, база знаний интегрируется с популярными CRM-системами (Битрикс24, amoCRM, 1С), облачными дисками, корпоративными сайтами и другими внутренними системами. Интеграция позволяет использовать единую точку доступа к информации из разных источников.
                    </AccordionContent>
                  </AccordionItem>
                </>
              )}
              {product.id === 'crm-integration' && (
                <>
                  <AccordionItem value="dev-1">
                    <AccordionTrigger>Что такое Vibe-coding методология разработки?</AccordionTrigger>
                    <AccordionContent>
                      Vibe-coding — это уникальная методология разработки, которая сочетает классические подходы к созданию корпоративного ПО с возможностями искусственного интеллекта. Позволяет создавать MVP за дни вместо месяцев, сохраняя при этом качество и надежность решений.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="dev-2">
                    <AccordionTrigger>Сколько времени занимает разработка AI-решения под заказ?</AccordionTrigger>
                    <AccordionContent>
                      Срок разработки зависит от сложности проекта. Простые AI-решения для автоматизации процессов создаются за 5-10 рабочих дней. Более сложные системы с глубокой интеграцией могут занять 2-4 недели. Мы всегда предоставляем детальный план работ с указанием сроков.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="dev-3">
                    <AccordionTrigger>Какие AI-технологии используются при разработке?</AccordionTrigger>
                    <AccordionContent>
                      Мы используем современные технологии искусственного интеллекта: нейросети для обработки естественного языка, машинное обучение для анализа данных, компьютерное зрение для работы с изображениями, а также интеграцию с популярными AI-платформами.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="dev-4">
                    <AccordionTrigger>Предоставляется ли поддержка после внедрения?</AccordionTrigger>
                    <AccordionContent>
                      Да, мы предоставляем полную техническую поддержку после внедрения решения. Включает исправление ошибок, обновления системы, консультации по использованию и развитие функционала по запросу клиента.
                    </AccordionContent>
                  </AccordionItem>
                </>
              )}
            </Accordion>
          </section>
        )}

        <TaskCTA
          title="Обсудите задачу с экспертом!"
          onCalculationClick={() => setIsCalculationFormOpen(true)}
          showRequestButton={false}
        />
      </main>

      <Footer />
      <ConsultantChatGate />
      <RequestPriceForm
        open={priceRequestOpen}
        onOpenChange={(open) => {
          setPriceRequestOpen(open);
          if (!open) setPriceRequestLicenseIndex(null);
        }}
        productName={product?.name}
        licenseName={
          priceRequestLicenseIndex !== null
            ? productLicenses[priceRequestLicenseIndex]?.name
            : null
        }
        calculatorData={
          priceRequestLicenseIndex !== null && product
            ? buildPriceRequestCalculatorData(
                product,
                productLicenses[priceRequestLicenseIndex],
                priceRequestLicenseIndex,
              )
            : null
        }
      />

      <CallbackForm
        open={expertContactOpen}
        onOpenChange={setExpertContactOpen}
        title="Свяжитесь со мной"
        description="Оставьте номер телефона или email — ответим и предложим время для консультации."
        submitLabel="Отправить"
        requireInn
      />

      <RequestCalculationForm
        open={isCalculationFormOpen}
        onOpenChange={setIsCalculationFormOpen}
        calculatorData={null}
      />
      
      {/* Попап с описанием услуги */}
      <AlertDialog open={!!selectedServiceForPopup} onOpenChange={() => setSelectedServiceForPopup(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{selectedServiceForPopup?.name}</AlertDialogTitle>
            <AlertDialogDescription className="text-base text-foreground">
              {selectedServiceForPopup?.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Закрыть</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Диалог авторизации для неавторизованных пользователей */}
      <AlertDialog open={showAuthDialog} onOpenChange={setShowAuthDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Требуется авторизация</AlertDialogTitle>
            <AlertDialogDescription>
              Для добавления товара в корзину необходимо войти в личный кабинет или зарегистрироваться. 
              Ваш заказ сохранён и будет добавлен в корзину после авторизации.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={() => navigate('/register')}>
              Зарегистрироваться
            </AlertDialogAction>
            <AlertDialogAction onClick={() => navigate('/login')}>
              Войти
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProductPageNew;
